import { eq, reject } from 'lodash/fp';
import { array } from 'fp-ts';
import { createSelector } from 'reselect';
import { Amount, ApplicationState, WithScope } from '@mews-commander/types';
import { getStringProp, togglePresence } from '@mews/fp';
import {
    AsyncMetaState,
    BusinessError,
    composeProps,
    createAsyncMetaSelectors,
    createPropsExtender,
    createScopedReducer,
    createScopedSelector,
    FeatureFlagService,
    Selector,
} from '@mews/framework';
import { isZeroAmount } from '@mews-commander/utils';
import {
    getBill,
    getOpenBillIdsByAssociatedAccountIds,
    getOpenBillIdsByOwnerIds,
} from '@mews-commander/legacy-entities/src/ducks/bills';
import { isAccountIdCompany } from '@mews-commander/legacy-entities/src/selectors/bills';
import {
    createEntityAsyncMetaHandlers,
    isCompanyInvoiceable,
    isCustomerInvoiceable,
} from '@mews-commander/legacy-entities/src/ducks';
import { hasSomeReceivableInvoicePayments } from '@mews-commander/legacy-entities/src/ducks/payments';
import { META_STATE_SUCCEEDED } from '@mews-commander/legacy-entities/src/constants';
import { BillingGrouping } from '../constants';
import { moduleSelector as parentSelector } from '../module';
import {
    CHANGE_BILLING_GROUPING,
    COLLAPSE_ALL,
    EXPAND_ALL,
    INITIALIZE_BILLING_FAILED,
    INITIALIZE_BILLING_STARTED,
    INITIALIZE_BILLING_SUCCEEDED,
    REPLACE_EXPANDED_BILL,
    SET_ACCOUNT_IDS,
    TOGGLE_EXPAND_BILL,
    TOGGLE_HIGHLIGHT_ORDER_ITEM,
} from '../actionTypes';

export const NAME = 'billing';

export const SCROLL_TO_BILL_ANCHOR = 'data-scroll-anchor';
export const SCROLL_TO_ORDER_ITEM_ANCHOR = 'data-scroll-order-item-anchor';

export interface BillingReducerState {
    accountIds: string[];
    expandedBillIds: string[];
    highlightedOrderItemId: string | null;
    billingGrouping: BillingGrouping;
    meta: AsyncMetaState;
}

interface ReplaceExpandedBillProps {
    fromBillId: string;
    toBillId: string;
}

export const initialState = {
    accountIds: [],
    expandedBillIds: [],
    billingGrouping: BillingGrouping.Reservation,
    ...META_STATE_SUCCEEDED,
};

export const billingReducer = createScopedReducer(initialState, {
    [SET_ACCOUNT_IDS]: (state: BillingReducerState, accountIds: string[]) => ({
        ...state,
        accountIds,
    }),
    ...createEntityAsyncMetaHandlers({
        STARTED: INITIALIZE_BILLING_STARTED,
        SUCCEEDED: INITIALIZE_BILLING_SUCCEEDED,
        FAILED: INITIALIZE_BILLING_FAILED,
    }),
    [EXPAND_ALL]: (state: BillingReducerState, billIds: string[]) => ({
        ...state,
        expandedBillIds: billIds,
    }),
    [TOGGLE_HIGHLIGHT_ORDER_ITEM]: (state: BillingReducerState, orderItemId: string) => ({
        ...state,
        highlightedOrderItemId: state.highlightedOrderItemId === orderItemId ? null : orderItemId,
    }),
    [COLLAPSE_ALL]: (state: BillingReducerState) => ({
        ...state,
        expandedBillIds: [],
    }),
    [REPLACE_EXPANDED_BILL]: (state: BillingReducerState, { fromBillId, toBillId }: ReplaceExpandedBillProps) => {
        if (!state.expandedBillIds.includes(fromBillId)) {
            return state;
        }
        return {
            ...state,
            expandedBillIds: [...reject(eq(fromBillId), state.expandedBillIds), toBillId],
        };
    },
    [TOGGLE_EXPAND_BILL]: (state: BillingReducerState, billId: string) => ({
        ...state,
        expandedBillIds: togglePresence(billId)(state.expandedBillIds),
    }),
    [CHANGE_BILLING_GROUPING]: (state: BillingReducerState, grouping: BillingGrouping) => ({
        ...state,
        billingGrouping: grouping,
    }),
});

const moduleSelector = createSelector(parentSelector, (state) => state[NAME]);
const getScope: Selector<ApplicationState, WithScope, BillingReducerState> = createScopedSelector({
    parentSelector: moduleSelector,
    initialState,
});
const getMeta = createSelector(getScope, (scope) => scope.meta);

export const [isFetching, hasFailed] = createAsyncMetaSelectors(getMeta);

export const getAccountIds = createSelector(getScope, (scope) => scope.accountIds);
const getHighlightedOrderItemId = createSelector(getScope, (scope) => scope.highlightedOrderItemId);

export const getAllOpenBillIds = composeProps(getOpenBillIdsByOwnerIds, createPropsExtender('ownerIds', getAccountIds));

export const getAllOpenAssociatedBillIds = composeProps(
    getOpenBillIdsByAssociatedAccountIds,
    createPropsExtender('associatedAccountIds', getAccountIds)
);

const getExpandedBillIds = createSelector(getScope, (scope) => scope.expandedBillIds);

export const isAllCollapsed = createSelector(getExpandedBillIds, array.isEmpty);
export const isBillExpanded = createSelector(getStringProp('billId'), getExpandedBillIds, (billId, expandedBillIds) =>
    expandedBillIds.includes(billId)
);

export const isOrderItemHighlighted = createSelector(
    getStringProp('orderItemId'),
    getHighlightedOrderItemId,
    (orderItemIdProp, orderItemIdState) => orderItemIdProp === orderItemIdState
);

export const isAnyOrderItemExpanded = (
    state: ApplicationState,
    { orderItemIds, scope }: { orderItemIds: string[] } & WithScope
) => orderItemIds.some((orderItemId) => isOrderItemHighlighted(state, { orderItemId, scope }));

interface IsBillInvoiceableParams {
    amount?: Amount;
    billId: string;
    featureFlagService: FeatureFlagService;
}

export const isBillInvoiceable = (state: ApplicationState, { billId, amount }: IsBillInvoiceableParams) => {
    const { ownerId } = getBill(state, { id: billId }) || {};
    const hasSomeInvoicePayments = hasSomeReceivableInvoicePayments(state, { billId });

    if (isZeroAmount(amount) || hasSomeInvoicePayments) return false;

    if (!ownerId) throw new BusinessError('ownerId is empty.');

    const isCompany = isAccountIdCompany(state, { accountId: ownerId });

    return isCompany ? isCompanyInvoiceable(state, { id: ownerId }) : isCustomerInvoiceable(state, { id: ownerId });
};

export const getBillingGrouping = createSelector(getScope, (scope) => scope.billingGrouping);
